import { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ChevronDown } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Slider } from '@/components/ui/slider';

type NetLivingAreaFilterSchemaType = {
  livingArea: [number, number];
};

type NetLivingAreaFilterProps = {
  livingArea?: {
    min: number;
    max: number;
  };
  onValueChange: (livingArea: { min: number; max: number }) => void;
};

export const NetLivingAreaFilter: React.FC<NetLivingAreaFilterProps> = ({
  livingArea = { min: 1, max: 300 },
  onValueChange,
}) => {
  const { t } = useTranslation();
  const [isOpened, setIsOpened] = useState(false);

  const methods = useForm<NetLivingAreaFilterSchemaType>({
    defaultValues: {
      livingArea: [livingArea.min, livingArea.max],
    },
  });

  const handleSaveFilter = () => {
    const [min, max] = methods.getValues('livingArea');
    onValueChange({ min, max });
    setIsOpened(false);
  };

  return (
    <Popover onOpenChange={setIsOpened} open={isOpened}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-sm border bg-primary-lighter">
          {t('netLivingArea')} m<sup>2</sup>
          <ChevronDown className="ml-1" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="start">
        <p className="mb-2 text-md text-primary">
          {t('netLivingArea')} m<sup>2</sup>
        </p>
        <FormProvider {...methods}>
          <Controller
            {...methods}
            name="livingArea"
            render={({ field: { value, ...field } }) => (
              <div>
                <div className="mb-2 flex justify-between md:w-auto">
                  <span className="rounded-xs bg-primary-light px-2 py-0.5 text-xs text-white">
                    {'< 10'}
                  </span>
                  <span className="rounded-xs bg-primary-light px-2 py-0.5 text-xs text-white">
                    {'> 300'}
                  </span>
                </div>
                <Slider
                  value={value}
                  max={300}
                  min={1}
                  step={1}
                  thumbs={2}
                  onValueChange={(ev) => {
                    field.onChange(ev);
                  }}
                />
                <div className="mt-4 flex gap-2">
                  <div>
                    <span className="select-none text-xs text-primary">{t('from')}</span>
                    <Input
                      size="md"
                      type="number"
                      placeholder={t('from')}
                      endIcon={
                        <div className="absolute right-[16px] top-[17px] block text-md text-primary-light xl:hidden">
                          m<sup>2</sup>
                        </div>
                      }
                      min={1}
                      value={value[0]}
                      {...field}
                      onChange={(ev) => {
                        field.onChange([ev.target.value, value[1]]);
                      }}
                    />
                  </div>
                  <div>
                    <span className="select-none text-xs text-primary">{t('to')}</span>
                    <Input
                      size="md"
                      type="number"
                      placeholder={t('to')}
                      endIcon={
                        <div className="absolute right-[16px] top-[17px] block text-md text-primary-light xl:hidden">
                          m<sup>2</sup>
                        </div>
                      }
                      max={300}
                      value={value[1]}
                      {...field}
                      onChange={(ev) => {
                        field.onChange([value[0], ev.target.value]);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          />
          <div className="mt-4 flex justify-end">
            <Button variant="contained" size="sm" className="rounded-lg" onClick={handleSaveFilter}>
              {t('save')}
            </Button>
          </div>
        </FormProvider>
      </PopoverContent>
    </Popover>
  );
};
